import { useCallback, useMemo } from 'react';
import { useValidationSchema } from '../../useValidationSchema/useValidationSchemaHook';
import { AnySchema } from 'yup';
import { ServerError, ValidateFn } from '../../../Interfaces/Validation';
import { KeyValue } from '../../../Interfaces/Common';

interface Input {
  schema: AnySchema<unknown, unknown, unknown>;
  serverErrors?: ServerError[];
}

interface Output<T> {
  validation: ValidateFn<T>;
}

export const useClientServerValidationHook = <TVal,>({
  schema,
  serverErrors = [],
}: Input): Output<TVal> => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const validate = useValidationSchema<TVal>(schema);

  const serverErrorsComputed = useMemo(() => {
    const errorObjects: KeyValue = {};

    serverErrors.forEach(({ field, message }) => {
      errorObjects[field] = message;
    });

    return errorObjects;
  }, [serverErrors]);

  const validation: ValidateFn<TVal> = useCallback(
    async values => {
      const clientErrorsComputed = await validate(values as TVal);

      return {
        ...(clientErrorsComputed || {}),
        ...(serverErrorsComputed || {}),
      } as Partial<TVal>;
    },
    [validate, serverErrorsComputed],
  );

  return {
    validation,
  };
};
